import { useState, useCallback, useRef, useEffect } from 'react';
import { apiService } from 'core/services/apiService';

/**
 * Backend API URL for OAuth initiation
 */
const BACKEND_API_URL = process.env.REACT_APP_API_URL || '';

/**
 * OAuth initiate response interface
 */
interface OAuthInitiateResponse {
	'@context'?: string;
	'@id'?: string;
	'@type'?: string;
	authUrl: string;
}

/**
 * Shared Drive interface
 */
interface SharedDrive {
	id: string;
	name: string;
}

/**
 * Partner Attempt Parameters interface
 */
interface PartnerAttemptParameters {
	access_token?: string;
	encrypted_refresh_token?: string;
	token_expires_in?: number;
	token_expires_at?: string;
	scope?: string;
	token_type?: string;
	shared_drives?: SharedDrive[];
	shared_drive_ids?: string[];
	drives_fetched_at?: string;
	drives_fetch_error?: string;
	tenant?: string;
	connected_by_token?: string;
}

/**
 * Partner Attempt interface
 */
interface PartnerAttempt {
	'@id': string;
	'@type': string;
	id: number;
	parameter: PartnerAttemptParameters;
	user: string;
	workplace: string;
	type: string;
	driveId: string | null;
	createdAt: string;
}

/**
 * Partner Attempts API response (Hydra Collection)
 */
interface PartnerAttemptsResponse {
	'@context'?: string;
	'@id'?: string;
	'@type'?: string;
	totalItems?: number;
	'hydra:totalItems'?: number;
	member?: PartnerAttempt[];
	'hydra:member'?: PartnerAttempt[];
	/** Hydra PartialCollectionView — present when more pages exist */
	view?: {
		'@id'?: string;
		'@type'?: string;
		next?: string;
		previous?: string;
		first?: string;
		last?: string;
	};
}

/**
 * Get authentication data from localStorage
 */
const getAuthData = () => {
	try {
		const byodApp = localStorage.getItem('BYOD-app');
		const organizationId = localStorage.getItem('BYOD-organizationId');
		
		if (byodApp) {
			const data = JSON.parse(byodApp);
			return {
				token: data.token_key || '',
				tenant: organizationId || '',
				workplace: data.workplace || null,
				userId: data.user?.id || null,
			};
		}
	} catch (error) {
		console.error('Error reading auth data from localStorage:', error);
	}
	return { token: '', tenant: '', workplace: null, userId: null };
};

interface GoogleAuthState {
	accessToken: string | null;
	encryptedRefreshToken: string | null;
	sharedDrives: SharedDrive[];
	selectedDriveId: string | null;
	isConnected: boolean;
	isLoading: boolean;
	error: string | null;
}

interface UseGoogleAuthReturn extends GoogleAuthState {
	initiateGoogleAuth: () => void;
	resetAuth: () => void;
	setSelectedDriveId: (driveId: string | null) => void;
}

/**
 * Custom hook for Google OAuth 2.0
 * Backend handles OAuth URL generation and PKCE
 * Polls partner_attempts API to check for successful connection
 */
export const useGoogleAuth = (): UseGoogleAuthReturn => {
	const [authState, setAuthState] = useState<GoogleAuthState>({
		accessToken: null,
		encryptedRefreshToken: null,
		sharedDrives: [],
		selectedDriveId: null,
		isConnected: false,
		isLoading: false,
		error: null,
	});
	const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const clearPollingTimers = useCallback(() => {
		if (pollTimeoutRef.current) {
			clearTimeout(pollTimeoutRef.current);
			pollTimeoutRef.current = null;
		}
		if (pollIntervalRef.current) {
			clearInterval(pollIntervalRef.current);
			pollIntervalRef.current = null;
		}
	}, []);

	useEffect(() => () => clearPollingTimers(), [clearPollingTimers]);

	/**
	 * Poll partner_attempts API to check for OAuth success.
	 * Follows Hydra view.next links to paginate through all records.
	 */
	const pollPartnerAttempts = useCallback(async (userId: number, workplaceId: number, startTime: Date): Promise<PartnerAttempt | null> => {
		let page = 1;

		try {
			let morePages = true;
			while (morePages) {
				const response = await apiService.get<PartnerAttemptsResponse>(
					`/partner_attempts`,
					{
						params: {
							page,
							itemsPerPage: 30,
							'user.id': userId,
							'workplace.id': workplaceId,
							type: 'google_drive',
						},
					}
				);

				const attempts = response.data.member || response.data['hydra:member'] || [];
				const totalItems = response.data.totalItems ?? response.data['hydra:totalItems'] ?? 0;

				console.log(`[OAuth] Page ${page}: fetched ${attempts.length} of ${totalItems} total attempts`);
				console.log('[OAuth] Start time for comparison:', startTime.toISOString());

				// Check this page for a matching attempt
				const recentAttempt = attempts.find((attempt) => {
					const attemptTime = new Date(attempt.createdAt);
					const hasToken = !!attempt.parameter?.access_token;
					const isAfterStart = attemptTime >= startTime;

					console.log('[OAuth] Checking attempt:', {
						id: attempt.id,
						createdAt: attempt.createdAt,
						attemptTime: attemptTime.toISOString(),
						hasToken,
						isAfterStart,
						matches: isAfterStart && hasToken,
					});

					return isAfterStart && hasToken;
				});

				if (recentAttempt) {
					console.log('[OAuth] Found matching attempt on page', page, '— ID', recentAttempt.id);
					return recentAttempt;
				}

				// Follow Hydra view.next link if present, otherwise we're on the last page
				if (response.data.view?.next) {
					page++;
				} else {
					console.log('[OAuth] No view.next — all pages exhausted, no matching attempt found');
					morePages = false;
				}
			}

			return null;
		} catch (error) {
			console.error('[OAuth] Error polling partner attempts:', error);
			return null;
		}
	}, [])

	/**
	 * Initiates Google OAuth flow
	 * Calls backend to get OAuth URL, then opens it in a popup
	 * Polls partner_attempts API to check for success
	 */
	const initiateGoogleAuth = useCallback(async () => {
		try {
			setAuthState((prev) => ({
				...prev,
				isLoading: true,
				error: null,
			}));

			// Get auth data from localStorage
			const { token, userId, workplace } = getAuthData();
			
			if (!token || !userId) {
				throw new Error('User not authenticated. Please log in again.');
			}

			const workplaceId = workplace ? (typeof workplace === 'object' ? workplace.id : workplace) : null;
			
			if (!workplaceId) {
				throw new Error('No workplace selected. Please select a workplace.');
			}

			console.log('[OAuth] Starting OAuth flow for user:', userId, 'workplace:', workplaceId);

			// Record the start time to filter attempts
			const startTime = new Date();

			// Call backend API to initiate OAuth and get the auth URL
			const response = await apiService.post<OAuthInitiateResponse>(
				'/oauth/google-drive/initiate',
				{} // Empty body as per requirements
			);

			const { authUrl } = response.data;

			if (!authUrl) {
				throw new Error('Failed to get OAuth URL from backend');
			}

			console.log('[OAuth] Opening OAuth window with URL:', authUrl);

			// Open OAuth URL in popup window
			const authWindow = window.open(
				authUrl,
				'google-oauth',
				'width=500,height=600,scrollbars=yes'
			);

			if (!authWindow) {
				throw new Error('Failed to open OAuth window. Please allow popups for this site.');
			}

			console.log('[OAuth] OAuth window opened, starting to poll for partner attempts');

			// Start polling for partner attempts immediately
			let pollCount = 0;
			const maxPolls = 60; // Poll for up to 5 minutes (60 * 5 seconds)
			const pollInterval = 5000; // Poll every 5 seconds

			// First poll immediately, then repeat every 5 seconds
			const doPoll = async () => {
				pollCount++;
				console.log(`[OAuth] Polling attempt ${pollCount}/${maxPolls} for user ${userId}, workplace ${workplaceId}`);

				// Poll the API
				const attempt = await pollPartnerAttempts(userId, workplaceId, startTime);

				if (attempt && attempt.parameter?.access_token) {
					console.log('[OAuth] Success! Partner attempt found (token present, not logged)');

					// Close the auth window. Retry a few times because cross-origin
					// navigation can make the first close() call race with the page load.
					const closeAuthWindow = () => {
						try {
							if (authWindow && !authWindow.closed) {
								authWindow.close();
							}
						} catch (e) {
							console.log('[OAuth] Could not close auth window:', e);
						}
					};
					console.log('[OAuth] Closing auth window');
					closeAuthWindow();
					setTimeout(closeAuthWindow, 500);
					setTimeout(closeAuthWindow, 1500);

					// Extract shared drives
					const sharedDrives = attempt.parameter.shared_drives || [];
					const selectedDrive = sharedDrives.length === 1 ? sharedDrives[0].id : null;

					console.log('[OAuth] Shared drives found:', sharedDrives.length);
					if (selectedDrive) {
						console.log('[OAuth] Auto-selected drive:', selectedDrive);
					}

					setAuthState({
						accessToken: attempt.parameter.access_token,
						encryptedRefreshToken: attempt.parameter.encrypted_refresh_token || null,
						sharedDrives: sharedDrives,
						selectedDriveId: selectedDrive,
						isConnected: true,
						isLoading: false,
						error: attempt.parameter.drives_fetch_error || null,
					});
					return 'stop';
				}

				// Check if we've exceeded max polls
				if (pollCount >= maxPolls) {
					console.log('[OAuth] Polling timeout reached after', pollCount, 'attempts');
					
					// Try to close the auth window if still open
					try {
						if (authWindow && !authWindow.closed) {
							authWindow.close();
						}
					} catch (e) {
						console.log('[OAuth] Could not close auth window on timeout:', e);
					}

					setAuthState((prev) => ({
						...prev,
						isLoading: false,
						error: 'Authentication timed out. Please try again.',
					}));
					return 'stop';
				}

				return 'continue';
			};

			// Do first poll after a brief delay (give window time to open)
			clearPollingTimers();
			pollTimeoutRef.current = setTimeout(async () => {
				console.log('[OAuth] Starting polling...');
				const result = await doPoll();
				
				if (result === 'stop') return;

				// Set up interval for subsequent polls
				pollIntervalRef.current = setInterval(async () => {
					const result = await doPoll();
					if (result === 'stop') {
						clearPollingTimers();
					}
				}, pollInterval);
			}, 500); // Start polling after just 500ms

		} catch (error: any) {
			console.error('[OAuth] Error initiating OAuth:', error);
			clearPollingTimers();
			setAuthState({
				accessToken: null,
				encryptedRefreshToken: null,
				sharedDrives: [],
				selectedDriveId: null,
				isConnected: false,
				isLoading: false,
				error: error.message || 'Failed to initiate Google authentication',
			});
		}
	}, [pollPartnerAttempts, clearPollingTimers]);

	/**
	 * Set the selected shared drive ID
	 */
	const setSelectedDriveId = useCallback((driveId: string | null) => {
		setAuthState((prev) => ({
			...prev,
			selectedDriveId: driveId,
		}));
	}, []);

	/**
	 * Resets authentication state
	 */
	const resetAuth = useCallback(() => {
		clearPollingTimers();
		setAuthState({
			accessToken: null,
			encryptedRefreshToken: null,
			sharedDrives: [],
			selectedDriveId: null,
			isConnected: false,
			isLoading: false,
			error: null,
		});
	}, [clearPollingTimers]);

	return {
		...authState,
		initiateGoogleAuth,
		resetAuth,
		setSelectedDriveId,
	};
};
